import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, User, Users, Building } from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  isCTA?: boolean;
  dropdown?: { label: string; path: string; description?: string }[];
}

const navItems: NavItem[] = [
  // { label: 'Home', path: '/' },
  { label: 'How It Works', path: '/how-it-works' },
  // {
  //   label: 'Solutions',
  //   path: '#',
  //   dropdown: [
  //     {
  //       label: 'Solo Agents & Teams',
  //       path: '/agents',
  //       description: 'Close more deals and expand your team'
  //     },
  //     {
  //       label: 'Brokerages',
  //       path: '/brokerages',
  //       description: 'Provide your agents with leads that close'
  //     }
  //   ]
  // },
  { label: 'Results', path: '/results' },
  // { label: 'Compare', path: '/compare' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'FAQ', path: '/faq' },
  // { label: 'Schedule Call →', path: '/schedule-call' },
  {
    label: 'Watch Demo',
    path: '/demo',
    isCTA: true,
    dropdown: [
      {
        label: 'Watch Video Now',
        path: '/instant-demo',
        description: 'Recently recorded demo'
      },
      {
        label: 'Book Live Demo with Expert',
        path: '/demo',
        description: 'Personalized screen share + ask questions'
      }
    ]
  }
];

interface NavigationProps {
  onLinkClick?: () => void;
  isScrolled?: boolean;
  logoMobile?: string;
}

export const Navigation = ({
  onLinkClick,
  isScrolled: _isScrolled = false,
  logoMobile = '/images/logo-blue.png'
}: NavigationProps) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const dropdownMenuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    // Check on mount
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Position dropdown and handle scroll/resize
  useEffect(() => {
    if (
      !openDropdown ||
      !dropdownRef.current ||
      !dropdownMenuRef.current ||
      isMobile
    ) {
      return;
    }

    const updatePosition = () => {
      const dropdownElement = dropdownMenuRef.current;
      const pointerElement = dropdownElement?.querySelector(
        '.nav-dropdown-pointer'
      ) as HTMLElement;

      if (dropdownElement && dropdownRef.current) {
        // Check if this is the CTA dropdown (Watch Demo) or Services dropdown
        const isCTADropdown = navItems.some(
          item => item.isCTA && item.path === openDropdown
        );

        if (isCTADropdown) {
          // Position CTA dropdown at specified coordinates
          dropdownElement.style.top = '100px';
          dropdownElement.style.left = '937px';
          dropdownElement.style.right = 'auto';

          // Position teardrop pointer at specified location
          if (pointerElement) {
            pointerElement.style.left = '298px';
          }
        } else {
          // Position Services dropdown relative to its menu item
          const menuItemRect = dropdownRef.current.getBoundingClientRect();
          dropdownElement.style.top = `${menuItemRect.bottom + 8}px`;
          dropdownElement.style.left = `${menuItemRect.left}px`;
          dropdownElement.style.right = 'auto';

          // Position teardrop pointer
          if (pointerElement) {
            pointerElement.style.left = '50px';
          }
        }
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, {
      passive: true,
      capture: true
    });
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, {
        capture: true
      });
      window.removeEventListener('resize', updatePosition);
    };
  }, [openDropdown, isMobile]);

  // Lock body scroll and ensure backdrop covers entire viewport when CTA dropdown is open
  useEffect(() => {
    // Only lock scroll for CTA dropdowns (Watch Demo), not for sub-menu dropdowns (Services)
    const ctaDropdown = navItems.find(
      item => item.isCTA && item.path === openDropdown
    );
    if (openDropdown && !isMobile && ctaDropdown) {
      // Store original overflow values
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;

      // Lock scroll
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      return () => {
        // Restore original overflow values
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
      };
    }
    return undefined;
  }, [openDropdown, isMobile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside CTA dropdown
      if (dropdownRef.current?.contains(target)) {
        return;
      }

      // Check if click is outside any sub-menu dropdown
      const clickedSubMenu = (target as Element).closest('.sub-menu');
      const clickedSubMenuParent = (target as Element).closest(
        'li[aria-haspopup="true"]'
      );

      if (!clickedSubMenu && !clickedSubMenuParent) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Close dropdown when user attempts to scroll (wheel events) - only for CTA dropdowns
  useEffect(() => {
    if (openDropdown && !isMobile) {
      const ctaDropdown = navItems.find(
        item => item.isCTA && item.path === openDropdown
      );
      if (ctaDropdown) {
        const handleWheel = () => {
          setOpenDropdown(null);
        };

        // Listen for wheel events (mouse wheel/trackpad scrolling)
        // Since scroll is locked, we detect scroll attempts via wheel events
        window.addEventListener('wheel', handleWheel, {
          passive: true,
          capture: true
        });
        document.addEventListener('wheel', handleWheel, {
          passive: true,
          capture: true
        });

        return () => {
          window.removeEventListener('wheel', handleWheel, { capture: true });
          document.removeEventListener('wheel', handleWheel, { capture: true });
        };
      }
    }
    return undefined;
  }, [openDropdown, isMobile]);

  const handleLinkClick = () => {
    // Scroll to top when clicking a navigation link
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onLinkClick?.();
    setOpenDropdown(null);
  };

  const toggleDropdown = (path: string) => {
    setOpenDropdown(openDropdown === path ? null : path);
  };

  // Pages that use light-hero variant (Results, Pricing, FAQ)
  const lightHeroPages = [
    '/results',
    '/pricing',
    '/faq',
    '/schedule-call',
    '/demo',
    '/terms',
    '/privacy',
    '/account'
  ];
  const isLightHeroPage = lightHeroPages.includes(location.pathname);

  // Filter nav items - show Home only on mobile
  const displayNavItems = isMobile
    ? [{ label: 'Home', path: '/' }, ...navItems]
    : navItems;

  return (
    <>
      {/* Backdrop overlay when CTA dropdown is open (not for sub-menu dropdowns) */}
      {(() => {
        const ctaDropdown = navItems.find(
          item => item.isCTA && item.path === openDropdown
        );
        return openDropdown && !isMobile && ctaDropdown ? (
          <div
            className="nav-dropdown-backdrop"
            onClick={() => setOpenDropdown(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              minHeight: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(1px)',
              WebkitBackdropFilter: 'blur(1px)',
              zIndex: 9999,
              transition: 'opacity 0.2s ease',
              overflow: 'hidden'
            }}
          />
        ) : null;
      })()}
      <nav
        className="wsmenu clearfix"
        style={isMobile ? { height: '928px' } : undefined}
      >
        <div className="overlapblackbg"></div>
        <ul className="wsmenu-list nav-theme">
          {/* Mobile Logo */}
          {isMobile && (
            <li className="mobile-menu-logo">
              <img src={logoMobile} alt="logo" />
            </li>
          )}
          {displayNavItems.map(item => {
            const isActive = location.pathname === item.path;

            // Handle dropdown items without CTA (like Services) - use nav-dropdown-menu style
            if (item.dropdown && item.dropdown.length > 0 && !item.isCTA) {
              const isDropdownOpen = openDropdown === item.path;
              const dropdownItems = item.dropdown;

              // Check if current path matches any dropdown item path (for Solutions menu)
              const isDropdownActive = dropdownItems.some(
                dropdownItem => location.pathname === dropdownItem.path
              );

              return (
                <li
                  key={item.path}
                  ref={dropdownRef}
                  aria-haspopup="true"
                  className={`${isDropdownOpen ? 'wsmenu-click' : ''} ${isDropdownActive ? 'active' : ''}`}
                  style={{ position: 'relative' }}
                >
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      toggleDropdown(item.path);
                    }}
                    className="h-link"
                  >
                    {item.label} <span className="wsarrow"></span>
                  </a>
                  <ul
                    ref={dropdownMenuRef}
                    className={`nav-dropdown-menu ${isDropdownOpen ? 'nav-dropdown-open' : ''}`}
                    style={{
                      ...(isDropdownOpen
                        ? {
                            display: 'block',
                            opacity: 1,
                            visibility: 'visible'
                          }
                        : {
                            display: 'none',
                            opacity: 0,
                            visibility: 'hidden',
                            pointerEvents: 'none' as const
                          })
                    }}
                  >
                    {/* Teardrop pointer */}
                    {isDropdownOpen && !isMobile && (
                      <div
                        className="nav-dropdown-pointer"
                        style={{ left: '50px' }}
                      />
                    )}
                    {dropdownItems.map(dropdownItem => {
                      // Determine icon based on label
                      let Icon;
                      if (dropdownItem.label === 'Solo Agents & Teams') {
                        Icon = Users;
                      } else if (dropdownItem.label === 'Brokerages') {
                        Icon = Building;
                      } else {
                        Icon = User; // fallback
                      }

                      return (
                        <li key={dropdownItem.path} aria-haspopup="true">
                          <Link
                            to={dropdownItem.path}
                            onClick={handleLinkClick}
                            className="nav-dropdown-link"
                          >
                            <div className="nav-dropdown-content">
                              <div className="nav-dropdown-icon">
                                <Icon size={20} />
                              </div>
                              <div className="nav-dropdown-text">
                                <span className="nav-dropdown-label">
                                  {dropdownItem.label}
                                </span>
                                {dropdownItem.description && (
                                  <span className="nav-dropdown-description">
                                    {dropdownItem.description}
                                  </span>
                                )}
                              </div>
                              {dropdownItem.label === 'Brokerages' && (
                                <span
                                  style={{
                                    fontSize: '8px',
                                    fontWeight: '600',
                                    padding: '1px 11px',
                                    borderRadius: '15px',
                                    backgroundColor: '#3b82f6',
                                    color: '#fff',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}
                                >
                                  Popular
                                </span>
                              )}
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            }

            if (item.isCTA) {
              // On mobile, always use blue background button
              // On desktop, use blue background button on light-hero pages (Results, Pricing, FAQ)
              // Blue background persists even when scrolled (btn--blue-400 handles this)
              // Otherwise use transparent white button
              const buttonClass =
                isMobile || isLightHeroPage
                  ? 'btn r-04 !rounded-[4px] btn--blue-400 hover--blue-500 last-link'
                  : 'btn r-04 !rounded-[4px] btn--tra-white hover--blue-500 last-link';

              // Check if this item has a dropdown
              if (item.dropdown && item.dropdown.length > 0) {
                const isDropdownOpen = openDropdown === item.path;

                return (
                  <li
                    key={item.path}
                    ref={dropdownRef}
                    aria-haspopup="true"
                    className={isDropdownOpen ? 'wsmenu-click' : ''}
                    style={{ position: 'relative' }}
                  >
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        toggleDropdown(item.path);
                      }}
                      className={buttonClass}
                      style={{ position: 'relative' }}
                    >
                      {item.label}{' '}
                      <span style={{ marginLeft: '4px' }}>
                        {isDropdownOpen ? '↓' : '→'}
                      </span>
                    </a>
                    <ul
                      ref={dropdownMenuRef}
                      className={`nav-dropdown-menu ${isDropdownOpen ? 'nav-dropdown-open' : ''}`}
                      style={{
                        ...(isDropdownOpen
                          ? {
                              display: 'block',
                              opacity: 1,
                              visibility: 'visible'
                            }
                          : {
                              display: 'none',
                              opacity: 0,
                              visibility: 'hidden',
                              pointerEvents: 'none' as const
                            })
                      }}
                    >
                      {/* Teardrop pointer */}
                      {isDropdownOpen && !isMobile && (
                        <div className="nav-dropdown-pointer" />
                      )}
                      {item.dropdown.map((dropdownItem, index) => (
                        <li key={dropdownItem.path} aria-haspopup="true">
                          <Link
                            to={dropdownItem.path}
                            onClick={handleLinkClick}
                            className="nav-dropdown-link"
                          >
                            <div className="nav-dropdown-content">
                              <div className="nav-dropdown-icon">
                                {index === 0 ? (
                                  <Clock size={20} />
                                ) : (
                                  <User size={20} />
                                )}
                              </div>
                              <div className="nav-dropdown-text">
                                <span className="nav-dropdown-label">
                                  {dropdownItem.label}
                                </span>
                                {dropdownItem.description && (
                                  <span className="nav-dropdown-description">
                                    {dropdownItem.description}
                                  </span>
                                )}
                              </div>
                              {index === 0 && (
                                <span className="nav-dropdown-badge">
                                  Length: 8-mins
                                </span>
                              )}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }

              return (
                <li key={item.path} className="nl-simple" aria-haspopup="false">
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={buttonClass}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            }

            return (
              <li
                key={item.path}
                className={`nl-simple ${isActive ? 'active' : ''}`}
                aria-haspopup="false"
              >
                <Link
                  to={item.path}
                  onClick={handleLinkClick}
                  className="h-link"
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
};
